import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, CheckCircle2, StickyNote, Send, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { DisputeThreadView } from "@/components/dispute/DisputeThreadView";
import { DisputeMeta } from "@/components/mod/DisputeMeta";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export function ModDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [selId, setSelId] = useState(null);
  const [sending, setSending] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [retractMsg, setRetractMsg] = useState(null);

  const retract = async () => {
    const m = retractMsg;
    setRetractMsg(null);
    const r = await api.post(`/mod/disputes/${selId}/messages/${m.id}/retract`);
    setDisputes((ds) => ds.map((d) => (d.id === selId ? r.data : d)));
    toast.success("Üzenet visszavonva");
  };

  const load = () => api.get("/mod/disputes").then((r) => setDisputes(r.data));
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const open = (d) => {
    setSelId(d.id);
    if (d.has_new_reporter_message) {
      api.patch(`/mod/disputes/${d.id}/seen`).catch(() => {});
      setDisputes((ds) => ds.map((x) => (x.id === d.id ? { ...x, has_new_reporter_message: false } : x)));
      window.dispatchEvent(new Event("disputes-seen"));
    }
  };
  const sorted = [...disputes].sort((a, b) => (b.has_new_reporter_message ? 1 : 0) - (a.has_new_reporter_message ? 1 : 0));

  const selected = disputes.find((d) => d.id === selId) || null;

  const send = async (text, files) => {
    setSending(true);
    try {
      const r = await api.post(`/mod/disputes/${selId}/messages`, { text, attachments: files });
      setDisputes((ds) => ds.map((d) => (d.id === selId ? r.data : d)));
    } finally {
      setSending(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const r = await api.post(`/mod/disputes/${selId}/notes`, { text: noteText.trim() });
      setDisputes((ds) => ds.map((d) => (d.id === selId ? r.data : d)));
      setNoteText("");
      toast.success("Belső jegyzet hozzáadva");
    } finally {
      setAddingNote(false);
    }
  };

  const setStatus = async (status) => {
    await api.patch(`/mod/disputes/${selId}/status`, { status });
    setDisputes((ds) => ds.map((d) => (d.id === selId ? { ...d, status } : d)));
    toast.success(status);
  };

  const saveProc = async (patch) => {
    const r = await api.patch(`/mod/disputes/${selId}/procedure`, patch);
    setDisputes((ds) => ds.map((d) => (d.id === selId ? r.data : d)));
    toast.success("Eljárási adatok mentve");
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-[420px] overflow-hidden rounded-lg border border-zinc-800/70 bg-[#111113]">
      <AlertDialog open={!!retractMsg} onOpenChange={(o) => !o && setRetractMsg(null)}>
        <AlertDialogContent className="border-zinc-800 bg-[#111113] text-zinc-100" data-testid="retract-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Üzenet visszavonása</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Az üzenet és a mellékletei teljesen eltűnnek a bejelentő elől. Az agentek továbbra is látják áthúzva. Ha az üzenetről már e-mail értesítés ment ki, azt nem lehet visszahívni.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="retract-cancel" className="border-zinc-700 bg-transparent text-zinc-300">Mégse</AlertDialogCancel>
            <AlertDialogAction data-testid="retract-confirm" onClick={retract} className="bg-red-600 text-white hover:bg-red-500">Visszavonás</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="w-72 shrink-0 overflow-y-auto border-r border-zinc-800/70">
        <div className="border-b border-zinc-800/70 px-4 py-2.5 label-caps">Disputes ({disputes.length})</div>
        {disputes.length === 0 && <div className="p-4 text-[13px] text-zinc-600">No disputes.</div>}
        {sorted.map((d) => (
          <button
            key={d.id}
            data-testid={`dispute-item-${d.id}`}
            onClick={() => open(d)}
            className={`block w-full border-b border-zinc-800/50 px-4 py-3 text-left transition-colors ${selId === d.id ? "bg-zinc-800/40" : "hover:bg-zinc-900/60"}`}
          >
            <div className="flex items-center gap-2">
              <MessageSquare size={14} className="text-zinc-500" />
              <span className="font-mono text-[13px] text-zinc-200">{d.report_reference}</span>
              {d.has_new_reporter_message && <span data-testid={`dispute-new-dot-${d.id}`} className="ml-auto inline-block h-2 w-2 shrink-0 rounded-full bg-blue-400" />}
            </div>
            {d.has_new_reporter_message && (
              <span data-testid={`dispute-new-tag-${d.id}`} className="mt-1 inline-block rounded border border-blue-500/40 bg-blue-500/10 px-1.5 py-0.5 text-[10px] text-blue-300">Új üzenet · {new Date(d.reporter_message_at).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", hour12: false, month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
            )}
            <div className="mt-1 flex items-center justify-between text-[11px]">
              <span data-testid={`dispute-item-status-${d.id}`} className={d.status === "escalated" ? "rounded bg-yellow-400/15 px-1.5 font-semibold text-yellow-300" : d.status === "open" ? "text-amber-400" : "text-emerald-400"}>{d.status}</span>
              <span className="text-zinc-600">{d.messages.length} msg</span>
            </div>
          </button>
        ))}
      </div>

      <div className="min-w-0 flex-1">
        {selected ? (
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-zinc-800/70 px-4 py-2.5">
              <div className="font-mono text-[13px] text-zinc-200">{selected.report_reference}</div>
              {selected.status === "open" ? (
                <Button data-testid="dispute-resolve" onClick={() => setStatus("resolved")} size="sm" className="h-8 bg-emerald-600 text-[12px] text-white hover:bg-emerald-700">
                  <CheckCircle2 size={14} /> Resolve
                </Button>
              ) : (
                <Button data-testid="dispute-reopen" onClick={() => setStatus("open")} variant="outline" size="sm" className="h-8 border-zinc-800 bg-zinc-900 text-[12px] text-zinc-300">
                  Reopen
                </Button>
              )}
            </div>
            <div key={selId} className="flex flex-wrap items-center gap-2 border-b border-zinc-800/70 bg-zinc-950/40 px-4 py-2" data-testid="dispute-procedure">
              <span className="text-[11px] uppercase tracking-wider text-zinc-500">Fázis</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`dispute-phase-${n}`} onClick={() => saveProc({ phase: n })}
                  className={`h-7 w-7 rounded-full text-[12px] font-bold ${(selected.phase || 1) === n && !selected.not_eligible ? "bg-blue-600 text-white" : "border border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>{n}</button>
              ))}
              <button data-testid="dispute-not-eligible" onClick={() => saveProc({ not_eligible: !selected.not_eligible })}
                className={`ml-2 rounded-md px-2.5 py-1 text-[12px] font-medium ${selected.not_eligible ? "bg-red-600 text-white" : "border border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>NEM jogosult</button>
              <div className="ml-auto flex gap-2">
                <Textarea data-testid="dispute-platform-response" defaultValue={selected.platform_response || ""} onBlur={(e) => e.target.value !== (selected.platform_response || "") && saveProc({ platform_response: e.target.value })} placeholder="Platform válasza…" className="h-9 min-h-9 w-44 resize-none border-zinc-800 bg-zinc-950 py-1.5 text-[11px] text-zinc-100" />
                <Textarea data-testid="dispute-adjudication" defaultValue={selected.adjudication || ""} onBlur={(e) => e.target.value !== (selected.adjudication || "") && saveProc({ adjudication: e.target.value })} placeholder="Döntés (adjudication)…" className="h-9 min-h-9 w-44 resize-none border-zinc-800 bg-zinc-950 py-1.5 text-[11px] text-zinc-100" />
              </div>
            </div>
            <DisputeMeta dispute={selected} onSave={saveProc} />
            <div className="flex min-h-0 flex-1">
              <div className="min-h-0 flex-1">
                <DisputeThreadView messages={selected.messages} onSend={send} sending={sending} side="agent" onRetract={setRetractMsg}
                  locked={["resolved", "escalated"].includes(selected.status) || (selected.phase || 1) >= 5 || !!selected.not_eligible}
                  lockedText={(selected.phase || 1) >= 5 || selected.not_eligible ? "A kommunikáció lezárva (5. fázis / NEM jogosult). Állítsd vissza a fázist a folytatáshoz." : "This dispute is closed. Reopen to continue the conversation."} labels={{ agent: "Agent", reporter: "Reporter", placeholder: "Reply to the reporter…" }} />
              </div>
              <aside data-testid="dispute-notes" className="hidden w-80 shrink-0 flex-col border-l border-zinc-800/70 bg-zinc-950/40 lg:flex">
                <div className="flex items-center gap-1.5 border-b border-zinc-800/70 px-4 py-2.5 label-caps">
                  <StickyNote size={13} className="text-amber-400" /> Belső jegyzetek
                  <span className="ml-auto flex items-center gap-1 text-[10px] normal-case text-zinc-600"><Lock size={10} /> csak moderátorok</span>
                </div>
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
                  {(selected.internal_notes || []).length === 0 && (
                    <div className="pt-6 text-center text-[12px] text-zinc-600">Még nincs belső jegyzet.</div>
                  )}
                  {[...(selected.internal_notes || [])].reverse().map((n) => (
                    <div key={n.id} className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-2.5">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="text-[11px] font-medium text-amber-300">{n.author}</span>
                        <span className="text-[10px] text-zinc-500">{new Date(n.created_at).toLocaleString()}</span>
                      </div>
                      <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-zinc-300">{n.text}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-zinc-800/70 p-3">
                  <Textarea
                    data-testid="dispute-note-input"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Belső jegyzet (a bejelentő nem látja)…"
                    className="min-h-[64px] resize-none border-zinc-800 bg-zinc-950 text-[12px] text-zinc-100"
                  />
                  <Button
                    data-testid="dispute-note-add"
                    onClick={addNote}
                    disabled={addingNote || !noteText.trim()}
                    className="mt-2 h-8 w-full gap-1.5 bg-amber-600 text-[12px] font-medium text-white hover:bg-amber-500"
                  >
                    <Send size={13} /> Jegyzet hozzáadása
                  </Button>
                </div>
              </aside>
            </div>
          </div>
        ) : (
          <div className="grid h-full place-items-center text-[13px] text-zinc-600">Select a dispute</div>
        )}
      </div>
    </div>
  );
}
